import React, { useState, useEffect } from 'react';
import { Pencil, Plus, Trash2, Check, X } from 'lucide-react';
import { sans, mono, green, red, useTheme } from '../../tokens';
import {
  getSpendCard, saveSpendCard, subscribeToSpendChanges,
} from '../../data/spendCardsStore';

// Consistent type sizes across the card (matches the RisksCard edit form)
const LABEL_SIZE = 10;
const INPUT_SIZE = 12;

function fmtK(n) {
  if (n == null || isNaN(n)) return '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

export default function EditableSpendCard({ companyId, selectedPeriod, type, readOnly = false }) {
  const t = useTheme();
  const [card, setCard] = useState(() => getSpendCard(companyId, selectedPeriod, type));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(card);

  useEffect(() => {
    setCard(getSpendCard(companyId, selectedPeriod, type));
  }, [companyId, selectedPeriod, type]);
  useEffect(() => subscribeToSpendChanges(() => setCard(getSpendCard(companyId, selectedPeriod, type))), [companyId, selectedPeriod, type]);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(card))); setEditing(true); };
  const cancelEdit = () => setEditing(false);
  const saveEdit = () => {
    saveSpendCard(companyId, selectedPeriod, type, draft);
    setCard(draft);
    setEditing(false);
  };

  const addItem = () => setDraft({ ...draft, items: [...draft.items, { label: 'New item', amount: 0 }] });
  const removeItem = (i) => setDraft({ ...draft, items: draft.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, patch) => setDraft({ ...draft, items: draft.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });

  const inputStyle = {
    padding: '6px 10px', borderRadius: 6, border: `1px solid ${t.border}`,
    background: t.bgCard, color: t.textPrimary, fontFamily: sans,
    fontSize: INPUT_SIZE, fontWeight: 400, outline: 'none',
    width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: LABEL_SIZE, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted,
    fontFamily: sans, marginBottom: 4,
  };

  if (editing) {
    const sumChildren = draft.items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    const over = sumChildren > draft.amount;
    return (
      <div style={{
        background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 10,
        padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <label style={labelStyle}>Card label</label>
        <input
          value={draft.label}
          onChange={e => setDraft({ ...draft, label: e.target.value })}
          style={{ ...inputStyle, marginBottom: 12 }}
        />

        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Amount ($)</label>
            <input
              type="number"
              value={draft.amount}
              onChange={e => setDraft({ ...draft, amount: Number(e.target.value) || 0 })}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>FY25 avg / mo ($)</label>
            <input
              type="number"
              value={draft.fyAvg}
              onChange={e => setDraft({ ...draft, fyAvg: Number(e.target.value) || 0 })}
              style={inputStyle}
            />
          </div>
          <div style={{ width: 110 }}>
            <label style={labelStyle}>Δ %</label>
            <input
              type="number"
              value={draft.fyDeltaPct}
              onChange={e => setDraft({ ...draft, fyDeltaPct: Number(e.target.value) || 0 })}
              style={inputStyle}
            />
          </div>
        </div>

        <label style={labelStyle}>Breakdown</label>
        <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
          {draft.items.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={it.label}
                onChange={e => updateItem(i, { label: e.target.value })}
                placeholder="Item"
                style={{ ...inputStyle, flex: 1 }}
              />
              <input
                type="number"
                value={it.amount}
                onChange={e => updateItem(i, { amount: Number(e.target.value) || 0 })}
                placeholder="Amount"
                style={{ ...inputStyle, width: 110 }}
              />
              <button onClick={() => removeItem(i)} title="Remove"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: red, padding: 4 }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={addItem} style={{
          padding: '5px 12px', borderRadius: 6,
          border: `1px dashed ${t.border}`, background: 'transparent',
          color: t.textMuted, fontFamily: sans, fontSize: INPUT_SIZE, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12,
        }}>
          <Plus size={12} /> Add item
        </button>

        {over && (
          <div style={{
            fontSize: LABEL_SIZE + 1, color: red, fontFamily: sans,
            padding: '6px 10px', background: red + '10', borderRadius: 4, marginBottom: 12,
          }}>
            Warning: sum of items ({fmtK(sumChildren)}) exceeds the total ({fmtK(draft.amount)}).
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancelEdit} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
            padding: '7px 14px', color: t.textSecondary,
            fontFamily: sans, fontSize: INPUT_SIZE, fontWeight: 600, cursor: 'pointer',
          }}><X size={13} /> Cancel</button>
          <button onClick={saveEdit} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: green, border: 'none', borderRadius: 6,
            padding: '7px 16px', color: '#fff',
            fontFamily: sans, fontSize: INPUT_SIZE, fontWeight: 600, cursor: 'pointer',
          }}><Check size={13} /> Save</button>
        </div>
      </div>
    );
  }

  // Read / display mode
  const deltaPositive = card.fyDeltaPct > 0;
  const deltaColor = card.deltaIsBad ? (deltaPositive ? red : green) : (deltaPositive ? green : red);
  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '20px 24px', position: 'relative',
    }}>
      {!readOnly && (
        <button onClick={startEdit} title="Edit card"
          style={{
            position: 'absolute', top: 10, right: 10,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: t.textMuted, padding: 4, opacity: 0.5,
          }}>
          <Pencil size={13} />
        </button>
      )}

      <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>
        {card.label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
        {fmtK(card.amount)}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
        {card.items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span style={{ fontSize: 11, fontFamily: sans, color: t.textSecondary }}>{it.label}</span>
            <span style={{ fontSize: 11, fontFamily: mono, color: t.textMuted }}>{fmtK(it.amount)}</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontFamily: sans, color: t.textMuted, marginBottom: 4 }}>
          <span>vs FY25 avg ({fmtK(card.fyAvg)}/mo)</span>
          <span style={{ color: deltaColor, fontWeight: 700, fontSize: 12 }}>
            {card.fyDeltaPct > 0 ? '+' : ''}{card.fyDeltaPct}%
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: t.borderLight, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${Math.min(100, Math.max(0, (card.amount / Math.max(card.amount, card.fyAvg * 1.5)) * 100))}%`,
            borderRadius: 3,
            background: 'linear-gradient(90deg, #718096, #4a5568)',
          }} />
        </div>
      </div>
    </div>
  );
}
